import { useState } from "react";
import { useAdmin } from "../admin";
import type { Site } from "../admin";
import { fileTag, useSignedUrl } from "../lib/files";
import { errorText, supabase } from "../supabase";

// The company's own settings. Chaukas serves many facility management companies, so
// the name and logo shown on every phone next to Chaukas come from here, never from
// the app itself.

const CLIENT_TYPES = [
  { value: "housing", name: "Housing society" },
  { value: "commercial", name: "Commercial property" },
  { value: "school", name: "School" },
  { value: "college", name: "College" },
];
const LOGO_TYPES: Record<string, string> = { "image/png": "png", "image/jpeg": "jpg", "image/webp": "webp" };

export default function Settings() {
  return (
    <>
      <div className="page-head">
        <h1>Settings</h1>
      </div>
      <div className="stack" style={{ gap: 20 }}>
        <Company />
        <Sites />
        <section className="card stack">
          <h2>Training leads</h2>
          <p className="muted">
            Training leads sign in here with an emailed link. To add or remove one, ask Chaukas support.
          </p>
        </section>
      </div>
    </>
  );
}

function Company() {
  const { org, reload } = useAdmin();
  const [name, setName] = useState(org.name);
  const [perWeek, setPerWeek] = useState(org.scenarios_per_week);
  const [logoPath, setLogoPath] = useState(org.logo_path);
  const [state, setState] = useState<{ kind: "ok" | "error" | ""; text: string }>({ kind: "", text: "" });
  const logo = useSignedUrl("client", logoPath);
  const changed = name.trim() !== org.name || perWeek !== org.scenarios_per_week || logoPath !== org.logo_path;

  async function upload(file: File) {
    const ext = LOGO_TYPES[file.type];
    if (!ext) return setState({ kind: "error", text: "Choose a PNG, JPEG or WebP picture." });
    if (file.size > 1024 * 1024) return setState({ kind: "error", text: "The logo must be under 1 MB." });
    setState({ kind: "", text: "Uploading…" });
    const path = `${org.id}/logo-${fileTag()}.${ext}`;
    const { error } = await supabase.storage.from("client").upload(path, file, { contentType: file.type });
    if (error) return setState({ kind: "error", text: errorText(error) });
    setLogoPath(path);
    setState({ kind: "", text: "Logo uploaded. Save to show it on phones." });
  }

  async function save() {
    const { error } = await supabase.rpc("admin_update_org", {
      p_name: name,
      p_logo_path: logoPath,
      p_scenarios_per_week: perWeek,
    });
    if (error) return setState({ kind: "error", text: errorText(error) });
    await reload();
    setState({ kind: "ok", text: "Saved. Phones show the new name and logo at their next sync." });
  }

  return (
    <section className="card stack">
      <h2>Company</h2>
      <label className="field">
        Company name
        <span className="hint">Shown on staff phones next to Chaukas.</span>
        <input type="text" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
      </label>
      <div className="field">
        Logo
        <span className="hint">PNG, JPEG or WebP, under 1 MB. It is shown as it is, never recoloured.</span>
        <div className="row">
          {logo ? (
            <img src={logo} alt="Company logo" style={{ maxHeight: 64, maxWidth: 240, background: "#fff", padding: 4, borderRadius: 8 }} />
          ) : (
            <span className="muted">{logoPath ? "Loading…" : "No logo. Phones show Chaukas only."}</span>
          )}
          <label className="btn quiet small">
            {logoPath ? "Replace logo" : "Upload logo"}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) upload(f);
              }}
            />
          </label>
          {logoPath && (
            <button className="link-btn small" onClick={() => setLogoPath(null)}>
              Remove logo
            </button>
          )}
        </div>
      </div>
      <label className="field" style={{ maxWidth: 260 }}>
        Scenarios per person each week
        <input
          type="number"
          min={1}
          max={10}
          value={perWeek}
          onChange={(e) => setPerWeek(Number(e.target.value))}
        />
      </label>
      <div className="row">
        <button className="btn" disabled={!changed || !name.trim() || perWeek < 1 || perWeek > 10} onClick={save}>
          Save
        </button>
        {state.text && <span className={`small ${state.kind === "error" ? "" : "muted"}`}>{state.text}</span>}
      </div>
    </section>
  );
}

function Sites() {
  const { sites, reload } = useAdmin();
  const [name, setName] = useState("");
  const [type, setType] = useState("housing");
  const [error, setError] = useState("");

  async function add(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    const { error } = await supabase.rpc("admin_save_site", { p_id: null, p_name: name, p_client_type: type });
    if (error) return setError(errorText(error));
    setName("");
    await reload();
  }

  return (
    <section className="card stack">
      <h2>Sites</h2>
      <p className="muted">The places your staff work. A roster file can only name sites listed here.</p>
      {sites.length === 0 ? (
        <p className="muted">No sites yet.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Kind</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sites.map((s) => (
              <SiteRow key={s.id} site={s} />
            ))}
          </tbody>
        </table>
      )}
      <form className="row" onSubmit={add}>
        <input type="text" placeholder="New site name" value={name} onChange={(e) => setName(e.target.value)} style={{ flex: 2 }} />
        <select aria-label="Kind of site" value={type} onChange={(e) => setType(e.target.value)} style={{ flex: 1 }}>
          {CLIENT_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.name}
            </option>
          ))}
        </select>
        <button className="btn" disabled={!name.trim()}>
          Add site
        </button>
      </form>
      {error && <div className="notice error">{error}</div>}
    </section>
  );
}

function SiteRow({ site }: { site: Site }) {
  const { reload } = useAdmin();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(site.name);
  const [type, setType] = useState(site.client_type ?? "");
  const [error, setError] = useState("");
  const kind = CLIENT_TYPES.find((t) => t.value === site.client_type)?.name ?? "–";

  async function save() {
    const { error } = await supabase.rpc("admin_save_site", { p_id: site.id, p_name: name, p_client_type: type || null });
    if (error) return setError(errorText(error));
    setEditing(false);
    setError("");
    await reload();
  }

  if (!editing) {
    return (
      <tr>
        <td>{site.name}</td>
        <td className="small">{kind}</td>
        <td style={{ textAlign: "right" }}>
          <button className="link-btn" onClick={() => setEditing(true)}>
            Rename
          </button>
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} aria-label="Site name" />
        {error && <div className="small" style={{ color: "var(--red)" }}>{error}</div>}
      </td>
      <td>
        <select value={type} onChange={(e) => setType(e.target.value)} aria-label="Kind of site">
          <option value="">–</option>
          {CLIENT_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.name}
            </option>
          ))}
        </select>
      </td>
      <td>
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button className="btn small" disabled={!name.trim()} onClick={save}>
            Save
          </button>
          <button className="btn quiet small" onClick={() => setEditing(false)}>
            Cancel
          </button>
        </div>
      </td>
    </tr>
  );
}
